import { apiClient } from "./apiClient";
import { DISTRICT_COORDINATES, calculateDistanceKm } from "../utils/locations";

export interface FetchShopsParams {
  limit?: number;
  category?: string;
  search?: string;
  location?: string;
  lat?: number | null;
  lng?: number | null;
}

export interface PopularShopItem {
  id: string;
  name: string;
  category: string;
  rating: string;
  offersCount: string;
  offerCount?: number;
  image: string;
  slug?: string;
  location?: string;
  distance?: string | null;
}

export const fallbackShops: PopularShopItem[] = [];

export async function fetchPopularShops(
  params?: number | FetchShopsParams
): Promise<PopularShopItem[]> {
  const queryParams: Record<string, string> = {};

  if (typeof params === "number") {
    queryParams.limit = String(params);
  } else if (params) {
    if (params.limit) queryParams.limit = String(params.limit);
    if (params.category && params.category !== "All" && params.category !== "All deals") {
      queryParams.category = params.category;
    }
    if (params.search?.trim()) queryParams.search = params.search.trim();
    const isAllLoc =
      !params.location ||
      params.location === "All" ||
      params.location.toLowerCase() === "all locations";
    if (!isAllLoc) {
      if (params.location?.trim() && params.location !== "Current Location") {
        queryParams.location = params.location.trim();
      }
      if (params.lat != null) queryParams.lat = String(params.lat);
      if (params.lng != null) queryParams.lng = String(params.lng);
    }
  } else {
    queryParams.limit = "4";
  }

  try {
    const res = await apiClient.get<any>(`/loyalty/public/popular-shops`, queryParams);

    if (res.success && res.data) {
      const items: any[] = Array.isArray(res.data)
        ? res.data
        : res.data.items || [];

      if (items.length > 0) {
        return items.map((shop, idx) => {
          const rawRating = shop.rating || shop.businessInfo?.rating;
          const ratingStr = rawRating ? Number(rawRating).toFixed(1) : "0";

          const offerCount = shop.offerCount ?? shop.offersCount;
          let formattedOffers = "Special Offers";
          if (typeof offerCount === "number") {
            formattedOffers = `${offerCount} offer${offerCount === 1 ? "" : "s"}`;
          } else if (typeof shop.offersCount === "string") {
            formattedOffers = shop.offersCount;
          }

          const image =
            shop.image ||
            shop.logo ||
            shop.businessInfo?.businessLogo ||
            shop.businessInfo?.coverImage ||
            "/deals/shop_vibe.jpg";

          return {
            id: shop._id || shop.id || `shop-${idx}`,
            name: shop.name || shop.businessName || "Partner",
            category:
              shop.category ||
              shop.businessType?.name ||
              (shop.serviceCategories && shop.serviceCategories[0]) ||
              "Store",
            rating: ratingStr,
            offersCount: formattedOffers,
            offerCount: typeof offerCount === "number" ? offerCount : 0,
            image,
            slug: shop.slug || shop.id || "",
            location: shop.location || shop.city || "Kerala",
            distance: shop.distance || null,
          };
        });
      }
    }

    // Try fallback to /loyalty/public/businesses
    const bizRes = await apiClient.get<any>(`/loyalty/public/businesses`, {
      sort: "popular",
      limit: queryParams.limit || "4",
      ...(queryParams.category ? { category: queryParams.category } : {}),
      ...(queryParams.search ? { search: queryParams.search } : {}),
    });

    if (bizRes.success && bizRes.data) {
      const items: any[] = Array.isArray(bizRes.data)
        ? bizRes.data
        : bizRes.data.items || [];

      if (items.length > 0) {
        return items.map((b, idx) => {
          const rawRating = b.rating || b.businessInfo?.rating;
          const ratingStr = rawRating ? Number(rawRating).toFixed(1) : "0";
          const count = b.offerCount || 0;

          return {
            id: b._id || `biz-${idx}`,
            name: b.businessName || "Partner",
            category:
              b.businessType?.name ||
              (b.serviceCategories && b.serviceCategories[0]) ||
              "Store",
            rating: ratingStr,
            offersCount: `${count} offer${count === 1 ? "" : "s"}`,
            offerCount: count,
            image: b.logo || b.businessInfo?.businessLogo || "/deals/shop_vibe.jpg",
            slug: b.slug || b._id || "",
            location: b.city || "Kerala",
            distance: null,
          };
        });
      }
    }
  } catch (err) {
    console.warn("Could not fetch popular shops from backend:", err);
  }

  // Return empty array if no shops found from backend
  return [];
}

export interface ShopDirectoryItem {
  id: string;
  slug: string;
  name: string;
  businessName: string;
  category: string;
  logo: string;
  coverImage: string;
  image: string;
  rating: number;
  reviewCount: number;
  serviceCategories: string[];
  offerCount: number;
  offersCount: string;
  branchCount: number;
  city: string;
  location: string;
  address: string;
  distance: string | null;
  distanceKm: number | null;
  phone?: string;
}

export interface ShopsDirectoryResponse {
  items: ShopDirectoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface FetchDirectoryParams {
  category?: string;
  search?: string;
  location?: string;
  type?: string;
  sort?: "newest" | "rating" | "offers" | "nearest" | "name";
  page?: number;
  limit?: number;
  lat?: number | null;
  lng?: number | null;
}

export async function fetchShopsDirectory(
  params?: FetchDirectoryParams
): Promise<ShopsDirectoryResponse> {
  const queryParams: Record<string, string> = {
    page: String(params?.page || 1),
    limit: String(params?.limit || 12),
  };

  if (params?.category && params.category !== "all" && params.category !== "All deals") {
    queryParams.category = params.category;
  }
  if (params?.search?.trim()) queryParams.search = params.search.trim();
  const isAllLocations =
    !params?.location ||
    params.location === "All" ||
    params.location.toLowerCase() === "all locations";
  const isCurrentLocation =
    Boolean(params?.location && params.location.toLowerCase() === "current location");

  if (isCurrentLocation) {
    if (params?.type) queryParams.type = params.type;
    queryParams.sort = "nearest";
    if (params?.lat != null) queryParams.lat = String(params.lat);
    if (params?.lng != null) queryParams.lng = String(params.lng);
  } else if (!isAllLocations) {
    if (params?.location?.trim()) {
      queryParams.location = params.location.trim();
    }
    if (params?.type) queryParams.type = params.type;
    if (params?.sort && params.sort !== "nearest") {
      queryParams.sort = params.sort;
    }
  } else {
    if (params?.type) queryParams.type = params.type;
    if (params?.sort && params.sort !== "nearest") {
      queryParams.sort = params.sort;
    }
  }

  try {
    let res = await apiClient.get<any>(`/loyalty/public/businesses`, queryParams);

    // If "Current Location" returned 18 or fewer shops due to old backend hexagon check,
    // fallback to querying all businesses so users can see all shops sorted by distance!
    if (
      isCurrentLocation &&
      res?.success &&
      res?.data?.pagination?.total &&
      res.data.pagination.total <= 18 &&
      (!params?.search || !params.search.trim()) &&
      (!params?.category || params.category === "all")
    ) {
      try {
        const fallbackRes = await apiClient.get<any>(`/loyalty/public/businesses`, {
          page: queryParams.page,
          limit: queryParams.limit,
          sort: "newest",
        });
        if (fallbackRes?.success && fallbackRes?.data?.items?.length) {
          res = fallbackRes;
        }
      } catch (e) {
        // keep res as is
      }
    }

    if (res.success && res.data) {
      const items: ShopDirectoryItem[] = (res.data.items || []).map((item: any, idx: number) => {
        let dist = item.distance || null;
        let distKm = typeof item.distanceKm === "number" ? item.distanceKm : null;

        // If distance is missing and user provided coordinates (e.g. Current Location or District):
        if (!dist && params?.lat != null && params?.lng != null) {
          const locStr = `${item.city || ""} ${item.location || ""} ${item.address || ""} ${item.district || ""}`.toLowerCase();
          let matchedCoords: { lat: number; lng: number } | null = null;
          for (const [dName, dCoords] of Object.entries(DISTRICT_COORDINATES)) {
            if (locStr.includes(dName.toLowerCase())) {
              matchedCoords = dCoords;
              break;
            }
          }
          if (!matchedCoords) {
            matchedCoords = DISTRICT_COORDINATES["Kottayam"];
          }
          const d = calculateDistanceKm(params.lat, params.lng, matchedCoords.lat, matchedCoords.lng);
          distKm = Number(d.toFixed(1));
          dist = `${distKm < 10 ? distKm.toFixed(1) : Math.round(distKm)} km`;
        }

        return {
          id: item._id || item.id || `shop-${idx}`,
          slug: item.slug || item._id,
          name: item.name || item.businessName || "Local Partner",
          businessName: item.businessName || item.name || "Local Partner",
          category: item.category || (item.serviceCategories && item.serviceCategories[0]) || "Partner Shop",
          logo: item.logo || "",
          coverImage: item.coverImage || item.image || "/deals/shop_vibe.jpg",
          image: item.coverImage || item.image || item.logo || "/deals/shop_vibe.jpg",
          rating: typeof item.rating === "number" ? Number(item.rating.toFixed(1)) : 0,
          reviewCount: item.reviewCount || 0,
          serviceCategories: item.serviceCategories || [],
          offerCount: item.offerCount || 0,
          offersCount: item.offersCount || `${item.offerCount || 0} offers`,
          branchCount: item.branchCount || 0,
          city: item.city || item.location || "",
          location: item.location || item.city || "",
          address: item.address || "",
          distance: dist,
          distanceKm: distKm,
          phone: item.phone || "",
        };
      });

      // If user selected Current Location, ensure items are sorted by distance
      if (isCurrentLocation && params?.lat != null && params?.lng != null) {
        items.sort((a, b) => {
          const da = a.distanceKm != null ? a.distanceKm : Infinity;
          const db = b.distanceKm != null ? b.distanceKm : Infinity;
          return da - db;
        });
      }

      return {
        items,
        pagination: res.data.pagination || {
          page: 1,
          limit: items.length,
          total: items.length,
          pages: 1,
        },
      };
    }
  } catch (err) {
    console.warn("fetchShopsDirectory error:", err);
  }

  // Return empty if no shops found from backend
  return {
    items: [],
    pagination: {
      page: 1,
      limit: 12,
      total: 0,
      pages: 0,
    },
  };
}

